import { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const useAuth = () => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Token validation utility
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return false;
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (payload.exp && payload.exp < currentTime) return false;
      
      return true;
    } catch (error) {
      return false;
    }
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!token || !isTokenValid(token)) {
      return Promise.reject(new Error('Authentication required'));
    }

    try {
      // Prepare headers - start with existing headers if any
      const headers = {
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      };

      // Only add Content-Type for non-FormData requests
      // FormData requests need the browser to set Content-Type automatically
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, {
        ...options,
        headers
      });

      return response;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  };

  // Initialize authentication on mount
  useEffect(() => {
    console.log('🔄 useAuth initialization...');
    const storedToken = localStorage.getItem('token');
    
    if (storedToken && isTokenValid(storedToken)) {
      console.log('✅ Valid token found, fetching user data...');
      setToken(storedToken);
      fetchUserData(storedToken);
    } else {
      console.log('❌ No valid token found');
      if (storedToken) {
        localStorage.removeItem('token');
        setToken(null);
      }
      setUser(null);
      setLoading(false);
    }
  }, []);

  // Update localStorage when token changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  // Fetch user data function
  const fetchUserData = async (tokenToUse = token) => {
    if (!tokenToUse || !isTokenValid(tokenToUse)) {
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 Fetching user data with token...');
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToUse}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ User data fetched successfully:', userData.user?.name);
        setUser(userData.user || userData);
      } else {
        console.warn('❌ Failed to fetch user data, clearing token');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle login
  const login = async (loginForm) => {
    try {
      console.log('🔐 Attempting login...');
      console.log('🔐 API_BASE_URL:', API_BASE_URL);
      console.log('🔐 Login URL:', `${API_BASE_URL}/api/login`);
      console.log('🔐 REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Login successful, setting token and user...');
        
        setToken(data.access_token);
        setUser(data.user);
        
        return { success: true, user: data.user };
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Login failed';
        
        // Check if account is disabled
        if (response.status === 403 && errorMessage.includes('disabled')) {
          return { success: false, error: errorMessage, accountDisabled: true };
        }
        
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Network error during login' };
    }
  };

  // Handle signup
  const signup = async (signupForm) => {
    try {
      console.log('🔐 Attempting signup...');
      const response = await fetch(`${API_BASE_URL}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Signup successful, setting token and user...');
        
        setToken(data.access_token);
        setUser(data.user);
        
        return { success: true, user: data.user };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Signup failed' };
      }
    } catch (error) {
      console.error('❌ Signup error:', error);
      return { success: false, error: 'Network error during signup' };
    }
  };

  // Handle logout
  const logout = () => {
    console.log('🔐 Logging out...');
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return {
    token,
    user,
    setUser,
    loading,
    setLoading,
    login,
    signup,
    logout,
    makeAuthenticatedRequest,
    fetchUserData,
    isTokenValid
  };
};